/**
 * Wire schemas for module 8 (subcontracts): packages and tendering, the
 * bidder's view of a package, the subcontract agreement and its mobilisation
 * checklist, payment applications and certificates (module 7's shapes, on the
 * subcontract), invoices, instructions, variations, back-charges, daily
 * reports, retention, the final account and ratings.
 *
 * Synced to the device: the subcontractor's daily reports (written offline;
 * editable narrative merged per field). Everything else is online. Money is
 * `{amount_minor, currency}`; quantities and percentages are decimal strings.
 * Optional fields default so a newer server never breaks an older client.
 */
import { z } from 'zod';

import {
  certificateDetailSchema,
  certificateSchema,
  commercialPartySchema,
  contractSchema,
  moneySchema,
  valuationDetailSchema,
  valuationLineSchema,
  valuationSchema,
} from './commercial-types';

const text = z.string().default('');
const date = z.string().nullable().default(null);
const nullableId = z.string().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const bool = z.boolean().catch(false).default(false);
const decimal = z.string().nullable().default(null);
const money = moneySchema.nullable().catch(null).default(null);
const person = z
  .object({ id: z.string(), name: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);

/** The actions the server says this person may take now (absent keys mean no). */
function can<const K extends string>(...keys: K[]): z.ZodType<Record<K, boolean>> {
  const pick = (raw: unknown) =>
    Object.fromEntries(
      keys.map((k) => [
        k,
        !!raw && typeof raw === 'object' && (raw as Record<string, unknown>)[k] === true,
      ]),
    );
  return z.unknown().transform(pick) as unknown as z.ZodType<Record<K, boolean>>;
}

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const PACKAGE_STATUSES = [
  'draft',
  'tendering',
  'evaluation',
  'award_pending',
  'awarded',
  'cancelled',
] as const;
export type PackageStatus = (typeof PACKAGE_STATUSES)[number];
export const PACKAGE_KINDS = ['domestic', 'nominated'] as const;
export type PackageKind = (typeof PACKAGE_KINDS)[number];
export const SUBCONTRACT_STATUSES = [
  'awaiting_acceptance',
  'mobilising',
  'active',
  'completed',
  'closed',
] as const;
export type SubcontractStatus = (typeof SUBCONTRACT_STATUSES)[number];
export const CHECKLIST_KINDS = [
  'insurance',
  'nca_registration',
  'method_statement',
  'risk_assessment',
  'induction',
  'site_access',
  'warranty',
  'other',
] as const;
export type ChecklistKind = (typeof CHECKLIST_KINDS)[number];
export const BACKCHARGE_CATEGORIES = [
  'back_charge',
  'contra_charge',
  'liquidated_damages',
  'other',
] as const;
export type BackchargeCategory = (typeof BACKCHARGE_CATEGORIES)[number];
/** Narrative sections of a daily report (offline three-way merge). */
export const SUB_REPORT_NARRATIVE = [
  'weather',
  'work_done',
  'materials',
  'delays',
  'safety',
  'notes',
] as const;
export type SubReportNarrativeField = (typeof SUB_REPORT_NARRATIVE)[number];
/** Every field a daily report's update may change. */
export const SUB_REPORT_EDITABLE = [...SUB_REPORT_NARRATIVE, 'manpower', 'task_ids'] as const;

// ---------------------------------------------------------------------------
// Packages and tenders (the contractor's view)
// ---------------------------------------------------------------------------

export const packageItemSchema = z.object({
  id: z.string(),
  sequence: count,
  boq_lineage: nullableId,
  section: text,
  item_code: text,
  description: text,
  unit: text,
  quantity: decimal,
  note: text,
});
export type PackageItem = z.infer<typeof packageItemSchema>;

const documentRefSchema = z.object({
  kind: z.string().default('drawing'),
  id: z.string(),
  reference: text,
  title: text,
  revision: text,
});
export type DocumentRef = z.infer<typeof documentRefSchema>;

const termsShape = {
  start_date: date,
  completion_date: date,
  tender_closes_at: date,
  retention_percent: decimal,
  retention_limit_percent: decimal,
  retention_pc_release_percent: count,
  advance_percent: decimal,
  advance_recovery_percent: decimal,
  payment_period_days: count,
  vat_percent: decimal,
  wht_percent: decimal,
  dlp_months: count,
  terms_note: text,
  documents: z.array(documentRefSchema).catch([]).default([]),
};

export const invitationSchema = z.object({
  id: z.string(),
  organization_id: nullableId,
  company_name: text,
  email: text,
  status: z.string().default('invited'),
  decline_reason: text,
  claimed: bool,
  created_at: date,
});
export type TenderInvitation = z.infer<typeof invitationSchema>;

export const tenderSummarySchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  company_name: text,
  status: z.string().default('submitted'),
  submitted_at: date,
  total: money,
  qualifications: z.string().nullable().default(null),
  programme_note: z.string().nullable().default(null),
  proposed_start: date,
  proposed_completion: date,
  validity_days: count,
});
export type TenderSummary = z.infer<typeof tenderSummarySchema>;

export const clarificationSchema = z.object({
  id: z.string(),
  number: count,
  question: text,
  answer: text,
  status: z.string().default('open'),
  shared: bool,
  addendum: bool,
  answered_at: date,
  created_at: date,
  mine: bool,
  company_name: z.string().nullable().default(null),
});
export type Clarification = z.infer<typeof clarificationSchema>;

export const packageSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  trade: text,
  scope: text,
  kind: z.string().default('domestic'),
  /** `subcontract` (module 8) or `main_contract` (module 9: the employer tenders the main contract). */
  level: z.string().default('subcontract'),
  buyer_name: text,
  status: z.string().default('draft'),
  currency: z.string().default('KES'),
  ...termsShape,
  sent_at: date,
  prepared_by: person,
  consent: z.string().default('not_required'),
  consent_note: text,
  consent_at: date,
  recommended_tender_id: nullableId,
  recommended_by: person,
  recommended_at: date,
  recommendation_note: text,
  awarded_tender_id: nullableId,
  awarded_by: person,
  awarded_at: date,
  subcontract_id: nullableId,
  /** The main contract a main-contract package was awarded into (module 9). */
  contract_id: nullableId,
  prices_open: bool,
  invitation_count: count,
  tender_count: count,
  created_at: date,
  can: can('edit', 'invite', 'send', 'close', 'recommend', 'approve', 'reject', 'cancel'),
});
export type Package = z.infer<typeof packageSchema>;

export const packageDetailSchema = packageSchema.extend({
  items: z.array(packageItemSchema).catch([]).default([]),
  invitations: z.array(invitationSchema).catch([]).default([]),
  tenders: z.array(tenderSummarySchema).catch([]).default([]),
  clarifications: z.array(clarificationSchema).catch([]).default([]),
});
export type PackageDetail = z.infer<typeof packageDetailSchema>;

export const comparisonSchema = z.object({
  currency: z.string().default('KES'),
  tenders: z
    .array(
      tenderSummarySchema.extend({
        normalised_total: money,
        missing_items: count,
        rank: count,
      }),
    )
    .catch([])
    .default([]),
  rows: z
    .array(
      z.object({
        item: packageItemSchema,
        average_rate: money,
        lowest_rate: money,
        cells: z
          .array(
            z.object({
              tender_id: z.string(),
              rate: money,
              amount: money,
              lowest: bool,
              missing: bool,
              normalised_amount: money,
              note: text,
            }),
          )
          .catch([])
          .default([]),
      }),
    )
    .catch([])
    .default([]),
});
export type Comparison = z.infer<typeof comparisonSchema>;

export const bidderOrgSchema = z.object({
  id: z.string(),
  name: text,
  kind: text,
  county: text,
  specialities: z.array(z.string()).catch([]).default([]),
  nca_category: text,
  verification_status: text,
});
export type BidderOrg = z.infer<typeof bidderOrgSchema>;

export const boqLineSchema = z.object({
  lineage: z.string(),
  section: text,
  item_code: text,
  description: text,
  unit: text,
  quantity: decimal,
  kind: text,
});
export type BoqLine = z.infer<typeof boqLineSchema>;

export const consentSchema = z.object({
  id: z.string(),
  reference: text,
  title: text,
  trade: text,
  scope: text,
  status: text,
  consent: z.string().default('pending'),
  consent_note: text,
  consent_by: person,
  consent_at: date,
  proposed_company: z.string().nullable().default(null),
  recommendation_note: text,
  start_date: date,
  completion_date: date,
});
export type Consent = z.infer<typeof consentSchema>;

// ---------------------------------------------------------------------------
// The bidder's view (organisation scope)
// ---------------------------------------------------------------------------

export const ownTenderSchema = z.object({
  id: z.string(),
  status: z.string().default('draft'),
  total: money,
  qualifications: text,
  programme_note: text,
  proposed_start: date,
  proposed_completion: date,
  validity_days: count,
  submitted_at: date,
  rates: z
    .array(z.object({ item_id: z.string(), rate: money, amount: money, note: text }))
    .catch([])
    .default([]),
});
export type OwnTender = z.infer<typeof ownTenderSchema>;

export const bidderPackageSchema = z.object({
  id: z.string(),
  reference: text,
  title: text,
  trade: text,
  scope: text,
  kind: text,
  buyer_name: text,
  project_name: text,
  site_location: text,
  currency: z.string().default('KES'),
  open: bool,
  status: z.string().default('open'),
  outcome: z.string().nullable().default(null),
  ...termsShape,
  invitation: z
    .object({ id: z.string(), status: z.string().default('invited'), decline_reason: text })
    .nullable()
    .catch(null)
    .default(null),
  tender: ownTenderSchema.nullable().catch(null).default(null),
  items: z.array(packageItemSchema).catch([]).default([]),
  clarifications: z.array(clarificationSchema).catch([]).default([]),
});
export type BidderPackage = z.infer<typeof bidderPackageSchema>;

// ---------------------------------------------------------------------------
// The subcontract
// ---------------------------------------------------------------------------

export const subcontractItemSchema = z.object({
  id: z.string(),
  section: text,
  item_code: text,
  description: text,
  unit: text,
  quantity: decimal,
  rate: money,
  amount: money,
});
export type SubcontractItem = z.infer<typeof subcontractItemSchema>;

export const checklistItemSchema = z.object({
  id: z.string(),
  stage: z.string().default('mobilisation'),
  kind: z.string().default('other'),
  sequence: count,
  title: text,
  required: bool,
  status: z.string().default('pending'),
  evidence: z.record(z.string(), z.unknown()).nullable().catch(null).default(null),
  compliance_document_id: nullableId,
  submittal_id: nullableId,
  reference: text,
  expires_on: date,
  note: text,
  response_note: text,
  submitted_by: person,
  submitted_at: date,
  verified_by: person,
  verified_at: date,
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const ratingSchema = z.object({
  id: z.string(),
  stage: z.string().default('milestone'),
  label: text,
  quality: count,
  programme: count,
  safety: count,
  cooperation: count,
  score: z.coerce.number().catch(0).default(0),
  comment: text,
  rated_by: person,
  rated_on: date,
});
export type Rating = z.infer<typeof ratingSchema>;

export const retentionSchema = z.object({
  held: money,
  released: money,
  due_stage: z.string().nullable().default(null),
  completion_date: date,
  dlp_end: date,
  released_stages: z.array(z.string()).catch([]).default([]),
  pc_release_percent: count,
  retention_percent: decimal,
  limit_percent: decimal,
});
export type SubRetention = z.infer<typeof retentionSchema>;

export const closeoutSchema = z.object({
  items: z
    .array(
      z.object({
        key: z.string(),
        label: text,
        done: bool,
        detail: text,
        required: bool,
        item_id: nullableId,
      }),
    )
    .catch([])
    .default([]),
  complete: bool,
});
export type Closeout = z.infer<typeof closeoutSchema>;

export const figuresSchema = z.object({
  contract_sum: money,
  revised_sum: money,
  certified_gross: money,
  certified_payable: money,
  paid: money,
  outstanding: money,
  retention_held: money,
  progress_percent: z.string().default('0'),
  open_backcharges: count,
  open_backcharges_value: money,
  agreed_backcharges_value: money,
  variations_pending: count,
  certificates_in_workflow: count,
  rating: z.number().nullable().catch(null).default(null),
  ratings: count,
  reports_submitted: count,
});
export type SubcontractFigures = z.infer<typeof figuresSchema>;

const SUBCONTRACT_CAN = [
  'edit',
  'accept',
  'mobilise',
  'complete',
  'close',
  'checklist',
  'submit_evidence',
  'rate',
  'apply',
  'value',
  'certify',
  'instruct',
  'backcharge',
  'propose_variation',
  'invoice',
] as const;

export const subcontractSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  trade: text,
  status: z.string().default('awaiting_acceptance'),
  nominated: bool,
  package_id: nullableId,
  contract_id: z.string().default(''),
  party: commercialPartySchema,
  contractor_party: commercialPartySchema,
  currency: z.string().default('KES'),
  contract_sum: money,
  start_date: date,
  completion_date: date,
  accepted_at: date,
  accepted_name: text,
  accepted_title: text,
  accepted_by: person,
  mobilised_at: date,
  practical_completion_on: date,
  dlp_end: date,
  closed_at: date,
  side: z.string().nullable().default(null),
  can: can(...SUBCONTRACT_CAN),
});
export type Subcontract = z.infer<typeof subcontractSchema>;

export const subcontractDetailSchema = subcontractSchema.extend({
  scope: text,
  special_conditions: text,
  terms: contractSchema.nullable().catch(null).default(null),
  dlp_months: z.number().nullable().catch(null).default(null),
  documents: z.array(documentRefSchema).catch([]).default([]),
  items: z.array(subcontractItemSchema).catch([]).default([]),
  checklist: z.array(checklistItemSchema).catch([]).default([]),
  ratings: z.array(ratingSchema).catch([]).default([]),
  closeout: closeoutSchema.nullable().catch(null).default(null),
  retention: retentionSchema.nullable().catch(null).default(null),
  figures: figuresSchema.nullable().catch(null).default(null),
});
export type SubcontractDetail = z.infer<typeof subcontractDetailSchema>;

export const overviewSchema = z.object({
  side: z.string().default('contractor'),
  subcontracts: z
    .array(
      subcontractSchema.extend({ figures: figuresSchema.nullable().catch(null).default(null) }),
    )
    .catch([])
    .default([]),
  packages: z.record(z.string(), z.number()).catch({}).default({}),
  reports_to_acknowledge: count,
  certificates_to_certify: count,
  payments_to_approve: count,
  reports_draft: count,
  instructions_open: count,
  backcharges_to_answer: count,
});
export type SubOverview = z.infer<typeof overviewSchema>;

export const complianceDocumentSchema = z.object({
  id: z.string(),
  doc_type: text,
  title: text,
  reference: text,
  expires_on: date,
  status: text,
});
export type ComplianceDocumentRef = z.infer<typeof complianceDocumentSchema>;

// ---------------------------------------------------------------------------
// Money: applications, certificates, invoices, the final account
// ---------------------------------------------------------------------------

export const subValuationSchema = valuationSchema.extend({
  subcontract_id: z.string().default(''),
});
export type SubValuation = z.infer<typeof subValuationSchema>;

export const subValuationDetailSchema = valuationDetailSchema.extend({
  subcontract_id: z.string().default(''),
  lines: z
    .array(valuationLineSchema.extend({ editable: z.boolean().catch(true).default(true) }))
    .catch([])
    .default([]),
  certificate: z
    .object({ id: z.string(), reference: text, status: text })
    .nullable()
    .catch(null)
    .default(null),
  can: can('edit'),
});
export type SubValuationDetail = z.infer<typeof subValuationDetailSchema>;

const subRefSchema = z
  .object({ id: z.string(), reference: text, title: text })
  .nullable()
  .catch(null)
  .default(null);

export const invoiceSchema = z.object({
  id: z.string(),
  subcontract_id: z.string().default(''),
  certificate_id: z.string().default(''),
  certificate_reference: text,
  number: text,
  invoice_date: date,
  amount: money,
  vat: money,
  etims_reference: text,
  note: text,
  status: z.string().default('submitted'),
  submitted_by: person,
  decided_by: person,
  decided_at: date,
  response_note: text,
  created_at: date,
});
export type SubInvoice = z.infer<typeof invoiceSchema>;

const SUB_CERT_CAN = [
  'edit',
  'submit',
  'cancel',
  'review',
  'certify',
  'return',
  'approve_payment',
  'record_payment',
] as const;

export const subCertificateSchema = certificateSchema.extend({
  subcontract: subRefSchema,
  payee: text,
  can: can(...SUB_CERT_CAN),
});
export type SubCertificate = z.infer<typeof subCertificateSchema>;

export const subCertificateDetailSchema = certificateDetailSchema.extend({
  subcontract: subRefSchema,
  payee: text,
  can: can(...SUB_CERT_CAN),
  invoices: z.array(invoiceSchema).catch([]).default([]),
});
export type SubCertificateDetail = z.infer<typeof subCertificateDetailSchema>;

export const subFinalAccountSchema = z.object({
  id: nullableId,
  status: z.string().nullable().default(null),
  figures: z.record(z.string(), moneySchema.nullable()).catch({}).default({}),
  note: text,
  prepared_by: person,
  submitted_at: date,
  contractor_agreed_at: date,
  subcontractor_agreed_at: date,
  can: can('prepare', 'submit', 'reopen', 'agree', 'dispute'),
});
export type SubFinalAccount = z.infer<typeof subFinalAccountSchema>;

export const subReconciliationSchema = z.object({
  currency: z.string().default('KES'),
  rows: z
    .array(
      z.object({
        certificate_id: z.string(),
        reference: text,
        kind: text,
        status: text,
        certified_at: date,
        due_on: date,
        payable: money,
        paid: money,
        outstanding: money,
        matches: bool,
      }),
    )
    .catch([])
    .default([]),
  totals: z.record(z.string(), moneySchema).catch({}).default({}),
  ledger: z
    .object({
      certificates_payable: money,
      retention_held: money,
      works: money,
      balanced: bool,
      reconciled: bool,
    })
    .nullable()
    .catch(null)
    .default(null),
});
export type SubReconciliation = z.infer<typeof subReconciliationSchema>;

// ---------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------

export const instructionSchema = z.object({
  id: z.string(),
  subcontract_id: z.string().default(''),
  number: count,
  reference: text,
  title: text,
  body: text,
  status: z.string().default('issued'),
  site_instruction_id: nullableId,
  main_variation_id: nullableId,
  issued_by: person,
  issued_on: date,
  acknowledged_by: person,
  acknowledged_at: date,
  response_note: text,
  can: can('acknowledge', 'close'),
});
export type SubInstruction = z.infer<typeof instructionSchema>;

export const subVariationSchema = z.object({
  id: z.string(),
  subcontract_id: z.string().default(''),
  number: count,
  reference: text,
  title: text,
  description: text,
  source: z.string().default('instruction'),
  instruction_id: nullableId,
  amount: money,
  days: count,
  status: z.string().default('proposed'),
  raised_by: person,
  raised_by_side: z.string().nullable().default(null),
  decided_by: person,
  decided_at: date,
  response_note: text,
  created_at: date,
  can: can('agree', 'dispute', 'revise', 'withdraw'),
});
export type SubVariation = z.infer<typeof subVariationSchema>;

export const backchargeSchema = z.object({
  id: z.string(),
  subcontract_id: z.string().default(''),
  number: count,
  reference: text,
  category: z.string().default('back_charge'),
  description: text,
  amount: money,
  status: z.string().default('proposed'),
  note: text,
  response_note: text,
  stock_movement_id: nullableId,
  raised_by: person,
  decided_by: person,
  decided_at: date,
  created_at: date,
  can: can('agree', 'dispute', 'revise', 'withdraw'),
});
export type Backcharge = z.infer<typeof backchargeSchema>;

export const storeIssueSchema = z.object({
  id: z.string(),
  number: count,
  item: text,
  unit: text,
  quantity: decimal,
  issued_at: date,
  received_by_name: text,
  suggested_amount: money,
  charged: bool,
});
export type StoreIssue = z.infer<typeof storeIssueSchema>;

// ---------------------------------------------------------------------------
// Daily reports (synced; written offline by the subcontractor)
// ---------------------------------------------------------------------------

export const manpowerRowSchema = z.object({ trade: text, count: count });
export type ManpowerRow = z.infer<typeof manpowerRowSchema>;

export const subReportSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  subcontract_id: nullableId,
  party_id: z.string().default(''),
  report_date: z.string(),
  status: z.string().default('draft'),
  author_id: nullableId,
  weather: text,
  work_done: text,
  materials: text,
  delays: text,
  safety: text,
  notes: text,
  manpower: z.array(manpowerRowSchema).catch([]).default([]),
  task_ids: z.array(z.string()).catch([]).default([]),
  submitted_at: date,
  acknowledged_at: date,
  acknowledged_by_id: nullableId,
  acknowledgement_note: text,
  version: z.number().int().nullable().default(null),
  updated_at: date,
  // Online list extras (absent on the synced copy).
  company_name: z.string().optional(),
  subcontract_title: z.string().nullable().optional(),
});
export type SubReport = z.infer<typeof subReportSchema>;

export const subReportRowSchema = subReportSchema.extend({
  author: person,
  acknowledged_by: person,
  can: can('edit', 'acknowledge'),
});
export type SubReportRow = z.infer<typeof subReportRowSchema>;

export const progressSchema = z.object({
  subcontract: subcontractSchema,
  figures: figuresSchema,
  valuation: z
    .object({ id: z.string(), number: count, status: text, date: date })
    .nullable()
    .catch(null)
    .default(null),
  items: z
    .array(
      subcontractItemSchema.extend({
        value_to_date: money,
        percent_complete: z.string().default('0'),
      }),
    )
    .catch([])
    .default([]),
  tasks: z
    .array(
      z.object({
        id: z.string(),
        title: text,
        status: text,
        due_date: date,
        planned_start: date,
        crew: z.string().nullable().default(null),
        zone: z.string().nullable().default(null),
      }),
    )
    .catch([])
    .default([]),
  task_counts: z.record(z.string(), z.number()).catch({}).default({}),
  reports: z.array(subReportRowSchema).catch([]).default([]),
});
export type SubProgress = z.infer<typeof progressSchema>;
